import { animate, inView, spring } from "motion";

class GestureCarousel {
  constructor(elementOrSelector) {
    this.container =
      typeof elementOrSelector === "string"
        ? document.querySelector(elementOrSelector)
        : elementOrSelector;

    this.wrapper = this.container.firstElementChild;
    this.items = Array.from(this.wrapper.children ?? []);

    if (this.items.length < 1) {
      throw "Carousel must have at least one child element";
    }

    this.itemWidth = this.calculateItemWidth();
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.animationInstance = null;
    this.autoPlaySpeed = -1;
    this.lastTimestamp = 0;

    // Momentum tracking
    this.lastDragX = 0;
    this.lastDragTime = 0;
    this.velocity = 0;
    this.momentumMultiplier = 50;

    // Store original sequence
    this.originalItems = [...this.items];
    this.nextItemIndex = 0;

    this.setupDOM();
    this.setupGestures();
    this.setupResizeHandling();
    this.startAutoPlay();
  }

  calculateItemWidth() {
    const item = this.items[0];
    const style = window.getComputedStyle(item);

    const width = item.offsetWidth;
    const marginLeft = parseFloat(style.marginLeft || "0");
    const marginRight = parseFloat(style.marginRight || "0");

    return width + marginLeft + marginRight;
  }

  /**
   * Calculates and updates the boundaries to center characters within the container.
   */
  calculateBounds() {
    const containerWidth = this.container.offsetWidth;
    const totalContentWidth = this.originalItems.length * this.itemWidth;

    // Calculate bounds to allow centering any character based on actual item positions
    const containerCenterX = containerWidth / 2;

    // Right boundary: position to center the first original character (index 0)
    const firstItemCenter = 0 * this.itemWidth + this.itemWidth / 2;
    this.maxX = containerCenterX - firstItemCenter;

    // Left boundary: position to center the last original character
    const lastItemCenter =
      (this.originalItems.length - 1) * this.itemWidth + this.itemWidth / 2;

    this.minX = containerCenterX - lastItemCenter;

    // Update arrow states after bounds are calculated
    this.updateArrowStates();
  }

  /**
   * Constrains the position within specified boundaries with elastic resistance.
   *
   * This function checks if the input position `x` exceeds the defined maximum (`maxX`) or falls below the minimum (`minX`).
   * If `x` is greater than `maxX`, it applies a 30% resistance, pulling back towards `maxX`.
   * Similarly, if `x` is less than `minX`, it applies a 30% resistance, pushing away from `minX`.
   * If `x` is within the boundaries, it returns `x` unchanged.
   *
   * @param {number} x - The current position to be constrained.
   */
  constrainPosition(x) {
    // Apply elastic resistance at boundaries
    if (x > this.maxX) {
      const excess = x - this.maxX;
      return this.maxX + excess * 0.3; // 30% resistance when overscrolling past right boundary
    }
    if (x < this.minX) {
      const excess = this.minX - x;
      return this.minX - excess * 0.3; // 30% resistance when overscrolling past left boundary
    }
    return x;
  }

  /**
   * Ensures a value is within specified bounds.
   */
  snapToBounds(x) {
    return Math.max(this.minX, Math.min(this.maxX, x));
  }
  /**
   * Resets and reinitializes the DOM by clearing, storing original items, and adding clones.
   */
  setupDOM() {
    // Clear and store original items
    const originalItems = [...this.items];
    this.wrapper.innerHTML = "";

    // Re-add original items
    originalItems.forEach((item) => {
      this.wrapper.appendChild(item);
    });

    // Add three sets of clones initially
    for (let i = 0; i < originalItems.length * 3; i++) {
      const clone = originalItems[i % originalItems.length].cloneNode(true);
      clone.setAttribute("aria-hidden", "true");
      this.wrapper.appendChild(clone);
    }

    // Calculate bounds first
    this.calculateBounds();

    // Set initial position to center the 10th character (index 9)
    const initialCenterIndex = Math.min(9, this.originalItems.length - 1); // Use 10th character if available
    const containerCenterX = this.container.offsetWidth / 2;
    const initialItemCenter =
      initialCenterIndex * this.itemWidth + this.itemWidth / 2;
    this.currentX = containerCenterX - initialItemCenter;

    // Apply bounds constraint to the initial position
    this.currentX = this.snapToBounds(this.currentX);
    this.wrapper.style.transform = `translateX(${this.currentX}px)`;

    // Initialize center focus after DOM is ready
    requestAnimationFrame(() => {
      this.updateCenterFocus();
    });
  }

  setupResizeHandling() {
    // Debounce helper
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.itemWidth = this.calculateItemWidth();
        this.checkAndRecycleItems();
      }, 250);
    };

    // Create resize observer
    this.resizeObserver = new ResizeObserver(handleResize);
    this.resizeObserver.observe(this.container);

    // Also handle window resize as fallback
    window.addEventListener("resize", handleResize);

    // Store handler reference for cleanup
    this.handleResize = handleResize;
  }

  startAutoPlay() {
    // Cancel any existing animation frame
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }

    // Cancel any existing momentum animation
    if (this.momentumAnimation) {
      this.momentumAnimation.stop();
    }

    const animate = () => {
      if (this.isDragging) return;

      const currentTime = performance.now();
      const deltaTime = currentTime - this.lastTimestamp;
      this.lastTimestamp = currentTime;

      // Move the carousel
      this.currentX += this.autoPlaySpeed;
      this.wrapper.style.transform = `translateX(${this.currentX}px)`;

      // Check if we need to recycle items
      this.checkAndRecycleItems();

      // Continue animation
      this.animationFrame = requestAnimationFrame(animate);
    };

    this.lastTimestamp = performance.now();
    this.animationFrame = requestAnimationFrame(animate);
  }

  checkAndRecycleItems() {
    const containerRect = this.container.getBoundingClientRect();
    const items = Array.from(this.wrapper.children);

    items.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();

      if (itemRect.right < containerRect.left) {
        const viewportWidth = containerRect.width;
        const itemsNeeded = Math.ceil(viewportWidth / this.itemWidth) + 2;

        const remainingItems = items.length - index;
        if (remainingItems > itemsNeeded) {
          this.wrapper.removeChild(item);

          const nextClone =
            this.originalItems[this.nextItemIndex].cloneNode(true);
          nextClone.setAttribute("aria-hidden", "true");
          this.wrapper.appendChild(nextClone);

          this.nextItemIndex =
            (this.nextItemIndex + 1) % this.originalItems.length;

          this.currentX += this.itemWidth;
          this.wrapper.style.transform = `translateX(${this.currentX}px)`;
        }
      }
    });

    // Always ensure we have enough items ahead
    const lastItemRect = this.wrapper.lastElementChild.getBoundingClientRect();
    if (lastItemRect.right < containerRect.right + containerRect.width) {
      const nextClone = this.originalItems[this.nextItemIndex].cloneNode(true);
      nextClone.setAttribute("aria-hidden", "true");
      this.wrapper.appendChild(nextClone);
      this.nextItemIndex = (this.nextItemIndex + 1) % this.originalItems.length;
    }
  }

  pauseAutoPlay() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  /**
   * Sets up event listeners for gesture controls and navigation.
   */
  setupGestures() {
    this.wrapper.addEventListener("mousedown", this.dragStart.bind(this));
    this.wrapper.addEventListener("touchstart", this.dragStart.bind(this));
    document.addEventListener("mousemove", this.dragMove.bind(this));
    document.addEventListener("touchmove", this.dragMove.bind(this));
    document.addEventListener("mouseup", this.dragEnd.bind(this));
    document.addEventListener("touchend", this.dragEnd.bind(this));
    document.addEventListener("mouseleave", this.dragEnd.bind(this));

    // Pause animation on hover
    this.container.addEventListener("mouseenter", () => {
      this.pauseAutoPlay();
    });

    // Set up arrow navigation if buttons exist
    this.setupArrowNavigation();

    // Initialize center focus
    this.updateCenterFocus();

    this.container.addEventListener("mouseleave", () => {
      if (!this.isDragging) {
        this.startAutoPlay();
      }
    });
  }

  /**
   * Sets up event listeners and updates arrow states for navigation.
   *
   * This function locates the previous and next buttons within a container,
   * binds click events to them, and ensures that the arrow states are updated accordingly.
   * It checks if the necessary elements exist before setting up the listeners.
   */
  setupArrowNavigation() {
    // Find arrow buttons by ID
    const containerId = this.container.id;
    if (!containerId) return;

    this.prevButton = document.getElementById(containerId + "_prev");
    this.nextButton = document.getElementById(containerId + "_next");

    if (this.prevButton) {
      this.prevButton.addEventListener(
        "click",
        this.scrollToPrevious.bind(this),
      );
    }

    if (this.nextButton) {
      this.nextButton.addEventListener("click", this.scrollToNext.bind(this));
    }

    // Update arrow states
    this.updateArrowStates();
  }

  /**
   * Scrolls to the previous item in the carousel.
   */
  scrollToPrevious() {
    if (this.isDragging) return;

    // Find current center item and move to previous
    const containerRect = this.container.getBoundingClientRect();
    const containerCenterX = containerRect.left + containerRect.width / 2;
    const items = Array.from(this.wrapper.children);

    let currentCenterIndex = 0;
    let closestDistance = Infinity;

    items.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      const itemCenterX = itemRect.left + itemRect.width / 2;
      const distance = Math.abs(itemCenterX - containerCenterX);

      if (distance < closestDistance) {
        closestDistance = distance;
        currentCenterIndex = index;
      }
    });

    // Map to original index and move to previous
    const currentOriginalIndex = currentCenterIndex % this.originalItems.length;
    const targetOriginalIndex = Math.max(0, currentOriginalIndex - 1);

    // Calculate target position
    const containerCenterXPos = this.container.offsetWidth / 2;
    const targetItemCenter =
      targetOriginalIndex * this.itemWidth + this.itemWidth / 2;
    const targetX = containerCenterXPos - targetItemCenter;
    const snappedX = this.snapToBounds(targetX);

    this.animateToPosition(snappedX);
  }

  /**
   * Scrolls to the next item in the carousel.
   */
  scrollToNext() {
    if (this.isDragging) return;

    // Find current center item and move to next
    const containerRect = this.container.getBoundingClientRect();
    const containerCenterX = containerRect.left + containerRect.width / 2;
    const items = Array.from(this.wrapper.children);

    let currentCenterIndex = 0;
    let closestDistance = Infinity;

    items.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      const itemCenterX = itemRect.left + itemRect.width / 2;
      const distance = Math.abs(itemCenterX - containerCenterX);

      if (distance < closestDistance) {
        closestDistance = distance;
        currentCenterIndex = index;
      }
    });

    // Map to original index and move to next
    const currentOriginalIndex = currentCenterIndex % this.originalItems.length;
    const targetOriginalIndex = Math.min(
      this.originalItems.length - 1,
      currentOriginalIndex + 1,
    );

    // Calculate target position
    const containerCenterXPos = this.container.offsetWidth / 2;
    const targetItemCenter =
      targetOriginalIndex * this.itemWidth + this.itemWidth / 2;
    const targetX = containerCenterXPos - targetItemCenter;
    const snappedX = this.snapToBounds(targetX);

    this.animateToPosition(snappedX);
  }

  /**
   * Animates the wrapper element to a new horizontal position with an ease-out effect.
   *
   * This function cancels any ongoing animations, calculates the distance and duration,
   * and then animates the movement using `requestAnimationFrame`. The animation uses an ease-out
   * cubic timing function to smooth the transition. After the animation completes, it updates
   * the arrow states and center focus.
   */
  animateToPosition(targetX) {
    // Guard against invalid values
    if (!isFinite(targetX) || !isFinite(this.currentX)) {
      console.warn(
        "GestureCarousel: Invalid position values detected in animateToPosition",
      );
      return;
    }

    // Stop any existing animation
    if (this.momentumAnimationId) {
      cancelAnimationFrame(this.momentumAnimationId);
    }

    const startX = this.currentX;
    const distance = Math.abs(targetX - startX);

    // If already at target, nothing to do
    if (distance < 1) return;

    const duration = 400; // Smooth animation duration
    const startTime = performance.now();

    /**
     * Animates a frame of the momentum scroll effect.
     */
    const animateFrame = (currentTime) => {
      try {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease out animation
        const easeOut = 1 - Math.pow(1 - progress, 3);
        const x = startX + (targetX - startX) * easeOut;

        // Guard against invalid calculated values
        if (isFinite(x)) {
          this.currentX = x;
          this.wrapper.style.transform = `translateX(${x}px)`;
          this.updateCenterFocus();
        }

        if (progress < 1) {
          this.momentumAnimationId = requestAnimationFrame(animateFrame);
        } else {
          // Ensure we're exactly at the target position
          this.currentX = targetX;
          this.wrapper.style.transform = `translateX(${this.currentX}px)`;
          this.momentumAnimationId = null;
          this.updateArrowStates();
          this.updateCenterFocus();
        }
      } catch (error) {
        console.warn("GestureCarousel: Animation frame error:", error);
        // Stop animation and set final position
        this.currentX = targetX;
        this.wrapper.style.transform = `translateX(${this.currentX}px)`;
        this.momentumAnimationId = null;
        this.updateArrowStates();
        this.updateCenterFocus();
      }
    };

    this.momentumAnimationId = requestAnimationFrame(animateFrame);
  }

  /**
   * Updates the states of the arrow buttons based on scroll position bounds.
   */
  updateArrowStates() {
    // Update arrow button states based on current position bounds
    if (this.prevButton) {
      // Disable previous when we can't scroll right anymore (at first character center)
      this.prevButton.disabled = this.currentX >= this.maxX - 5; // Small tolerance for floating point
    }

    if (this.nextButton) {
      // Disable next when we can't scroll left anymore (at last character center)
      this.nextButton.disabled = this.currentX <= this.minX + 5; // Small tolerance for floating point
    }
  }

  /**
   * Updates the focus styling of items within a container to ensure uniform size and opacity.
   */
  updateCenterFocus() {
    const containerRect = this.container.getBoundingClientRect();
    const containerCenterX = containerRect.left + containerRect.width / 2;
    const items = Array.from(this.wrapper.children);

    // Ensure all items have uniform styling - no scale or opacity variations
    items.forEach((item) => {
      // Remove any existing scale/opacity classes
      item.classList.remove(
        "scale-[1.15]",
        "opacity-100",
        "z-20",
        "scale-75",
        "opacity-60",
        "z-10",
      );

      // Apply uniform styling to all items
      item.classList.add("opacity-100", "z-10");

      // Reset character image and name styles to uniform state
      const characterImage = item.querySelector(".character-image");
      const characterName = item.querySelector(".character-name");
      if (characterImage) {
        characterImage.classList.remove("scale-[1.15]");
      }
      if (characterName) {
        characterName.classList.remove("text-lg", "font-bold", "px-4", "py-2");
      }
    });
  }

  // Calculate the position needed to center a specific original item
  /**
   * Calculates the center position of an item within a container.
   */
  getItemCenterPosition(originalItemIndex) {
    const containerCenterX = this.container.offsetWidth / 2;
    const itemCenterOffset =
      originalItemIndex * this.itemWidth + this.itemWidth / 2;
    return containerCenterX - itemCenterOffset;
  }

  // Find the closest item to center and snap to it
  /**
   * Snaps the current position to the nearest center of an item within the container.
   */
  snapToNearestCenter() {
    const containerRect = this.container.getBoundingClientRect();
    const containerCenterX = containerRect.left + containerRect.width / 2;
    const items = Array.from(this.wrapper.children);

    let closestIndex = 0;
    let closestDistance = Infinity;

    items.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      const itemCenterX = itemRect.left + itemRect.width / 2;
      const distance = Math.abs(itemCenterX - containerCenterX);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    // Map the found index to original items array (handle clones)
    const originalIndex = closestIndex % this.originalItems.length;

    // Calculate target position to center this original character
    const containerCenterXPos = this.container.offsetWidth / 2;
    const targetItemCenter =
      originalIndex * this.itemWidth + this.itemWidth / 2;
    const targetX = containerCenterXPos - targetItemCenter;
    const snappedX = this.snapToBounds(targetX);

    if (Math.abs(this.currentX - snappedX) > 5) {
      this.animateToPosition(snappedX);
    }
  }

  /**
   * Handles the start of a drag event.
   */
  dragStart(event) {
    if (event.target.closest("button") || event.target.closest("a")) {
      this.isDragging = false;
      return;
    }

    const startPosition = this.getPositionX(event);

    // Guard against invalid position values
    if (!isFinite(startPosition)) {
      console.warn("GestureCarousel: Invalid start position detected");
      return;
    }

    this.isDragging = true;
    this.startX = startPosition;

    // Initialize momentum tracking
    this.lastDragX = this.startX;
    this.lastDragTime = performance.now();
    this.velocity = 0;

    this.wrapper.classList.add("cursor-grabbing");
    this.wrapper.classList.remove("cursor-grab");

    this.pauseAutoPlay();

    try {
      // Get current transform value
      const transform = getComputedStyle(this.wrapper).transform;
      const matrix = new DOMMatrix(transform);
      const transformX = matrix.m41;

      // Guard against invalid transform values
      if (isFinite(transformX)) {
        this.currentX = transformX;
      }
    } catch (error) {
      console.warn(
        "GestureCarousel: Failed to get current transform, using existing currentX:",
        error,
      );
      // Keep existing this.currentX value as fallback
    }
  }

  dragMove(event) {
    if (!this.isDragging) return;

    event.preventDefault();
    const currentPosition = this.getPositionX(event);
    const currentTime = performance.now();

    // Guard against invalid position values
    if (!isFinite(currentPosition) || !isFinite(currentTime)) {
      console.warn("GestureCarousel: Invalid drag position values detected");
      return;
    }

    // Improved velocity calculation using time window
    const dt = Math.max(1, currentTime - this.lastDragTime); // Prevent division by zero
    const dx = currentPosition - this.lastDragX;

    // Guard against invalid calculations
    if (!isFinite(dx) || !isFinite(dt)) {
      return;
    }

    // Smooth out velocity calculation
    const newVelocity = dx / dt;

    // Guard against invalid velocity
    if (isFinite(newVelocity)) {
      this.velocity = this.velocity * 0.7 + newVelocity * 0.3; // Weighted average
    }

    // Update tracking variables
    this.lastDragX = currentPosition;
    this.lastDragTime = currentTime;

    const diff = currentPosition - this.startX;

    // Guard against invalid diff values
    if (isFinite(diff)) {
      this.currentX = this.currentX + diff;
      this.startX = currentPosition;

      // Guard against invalid currentX
      if (isFinite(this.currentX)) {
        this.wrapper.style.transform = `translateX(${this.currentX}px)`;
        this.checkAndRecycleItems();
      }
    }
  }

  dragEnd() {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.wrapper.classList.add("cursor-grab");
    this.wrapper.classList.remove("cursor-grabbing");

    // Calculate final momentum with damping
    const momentumDistance =
      this.velocity *
      this.momentumMultiplier *
      (Math.abs(this.velocity) < 0.5 ? 0 : 1);
    const targetX = this.currentX + momentumDistance;

    // Stop any existing animation
    if (this.momentumAnimation) {
      this.momentumAnimation.stop();
    }

    // Use the current position as the starting point
    const startX = this.currentX;

    // Guard against invalid values that could cause animation errors
    if (!isFinite(startX) || !isFinite(targetX) || !isFinite(this.velocity)) {
      console.warn(
        "GestureCarousel: Invalid animation values detected, falling back to auto-play",
      );
      requestAnimationFrame(() => {
        if (!this.isDragging) {
          this.startAutoPlay();
        }
      });
      return;
    }

    // If movement is very small, just restart auto-play
    if (Math.abs(targetX - startX) < 1) {
      requestAnimationFrame(() => {
        if (!this.isDragging) {
          this.startAutoPlay();
        }
      });
      return;
    }

    try {
      // Animate with momentum
      this.momentumAnimation = animate(
        this.wrapper,
        {
          x: [startX, targetX], // Explicitly define start and end points
        },
        {
          duration: Math.min(Math.abs(momentumDistance) * 2, 1000), // Duration based on distance
          easing: spring({
            stiffness: 100,
            damping: 25,
            mass: 1,
            restSpeed: 0.001,
            restDelta: 0.01,
          }),
          onUpdate: ({ x }) => {
            // Guard against invalid x values during animation
            if (isFinite(x)) {
              this.currentX = x;
              this.wrapper.style.transform = `translateX(${x}px)`;
              this.checkAndRecycleItems();
            }
          },
          onComplete: () => {
            // Ensure we're exactly at the target position
            this.currentX = targetX;
            this.wrapper.style.transform = `translateX(${this.currentX}px)`;

            requestAnimationFrame(() => {
              if (!this.isDragging) {
                this.startAutoPlay();
              }
            });
          },
        },
      );
    } catch (error) {
      console.warn(
        "GestureCarousel: Animation failed, falling back to direct positioning:",
        error,
      );

      // Fallback to direct positioning without animation
      this.currentX = targetX;
      this.wrapper.style.transform = `translateX(${this.currentX}px)`;

      requestAnimationFrame(() => {
        if (!this.isDragging) {
          this.startAutoPlay();
        }
      });
    }
  }

  getPositionX(event) {
    return event.type.includes("mouse")
      ? event.pageX
      : event.touches[0].clientX;
  }

  destroy() {
    this.pauseAutoPlay();

    // Stop momentum animation if exists
    if (this.momentumAnimation) {
      this.momentumAnimation.stop();
    }

    // Clean up resize handling
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    window.removeEventListener("resize", this.handleResize);

    // Remove event listeners
    this.wrapper.removeEventListener("mousedown", this.dragStart);
    this.wrapper.removeEventListener("touchstart", this.dragStart);
    document.removeEventListener("mousemove", this.dragMove);
    document.removeEventListener("touchmove", this.dragMove);
    document.removeEventListener("mouseup", this.dragEnd);
    document.removeEventListener("touchend", this.dragEnd);
    document.removeEventListener("mouseleave", this.dragEnd);

    // Reset to original content
    this.wrapper.innerHTML = "";
    this.originalItems.forEach((item) => this.wrapper.appendChild(item));

    // Reset styles
    this.wrapper.style.transform = "";
    this.wrapper.classList.remove("cursor-grabbing");
    this.wrapper.classList.remove("cursor-grab");
  }
}

export { GestureCarousel };
